import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp, TrendingDown, Package, Truck, RotateCcw, Users, Sparkles, Send, RefreshCw, AlertTriangle, Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (n) => (n === null || n === undefined) ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

const TABS = [
  { id: 'profit', label: 'Profit', icon: TrendingUp },
  { id: 'products', label: 'Products', icon: Package },
  { id: 'inventory', label: 'Inventory', icon: AlertTriangle },
  { id: 'operations', label: 'Operations', icon: Truck },
  { id: 'customers', label: 'Customers', icon: Users },
  { id: 'advisor', label: 'AI Advisor', icon: Sparkles },
];
const RANGES = [7, 30, 90, 365];

const Kpi = ({ label, value, sub, accent }) => (
  <div className="bg-wt-surface border border-wt-border rounded-xl p-4" data-testid={`kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <p className="text-xs text-wt-text-muted uppercase tracking-wide">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${accent || 'text-wt-text-primary'}`}>{value}</p>
    {sub && <p className="text-xs text-wt-text-muted mt-1">{sub}</p>}
  </div>
);

const Section = ({ title, children, right }) => (
  <div className="bg-wt-surface border border-wt-border rounded-xl p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-wt-text-primary">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const ProdCell = ({ r }) => (
  <div className="flex items-center gap-2 min-w-0">
    {r.image_url ? <img src={r.image_url} alt="" className="h-8 w-8 rounded object-cover shrink-0" />
      : <div className="h-8 w-8 rounded bg-wt-bg border border-wt-border shrink-0" />}
    <div className="min-w-0">
      <p className="truncate max-w-[200px] text-wt-text-primary" title={r.name}>{r.name || r.sku}</p>
      <p className="text-[10px] text-wt-text-muted truncate max-w-[200px]">{r.sku}{r.variant_title ? ` · ${r.variant_title}` : ''}</p>
    </div>
  </div>
);

const MiniTable = ({ cols, rows, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-wt-text-muted border-b border-wt-border">
          {cols.map(c => <th key={c.key} className={`py-2 pr-3 ${c.right ? 'text-right' : ''}`}>{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="py-4 text-center text-wt-text-muted text-xs">No data</td></tr>}
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-wt-border/40">
            {cols.map(c => (
              <td key={c.key} className={`py-2 pr-3 ${c.right ? 'text-right' : ''} ${c.cls ? c.cls(r) : 'text-wt-text-primary'}`}>
                {c.render ? c.render(r) : r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AnalyticsPage() {
  const [tab, setTab] = useState('profit');
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [pnl, setPnl] = useState(null);
  const [channels, setChannels] = useState(null);
  const [products, setProducts] = useState(null);
  const [inv, setInv] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [returnsL, setReturnsL] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [missingCosts, setMissingCosts] = useState(null);
  const [costEdits, setCostEdits] = useState({});
  const [savingSku, setSavingSku] = useState(null);
  // advisor
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const chatEndRef = useRef(null);

  const load = useCallback(async (t, d) => {
    setLoading(true);
    try {
      if (t === 'profit') {
        const [a, b, c] = await Promise.all([
          axios.get(`${API}/analytics/pnl?days=${d}`, auth()),
          axios.get(`${API}/analytics/channel-profit?days=${d}`, auth()),
          axios.get(`${API}/analytics/missing-costs`, auth()),
        ]);
        setPnl(a.data); setChannels(b.data); setMissingCosts(c.data);
      } else if (t === 'products') {
        const r = await axios.get(`${API}/analytics/product-profit?days=${d}`, auth());
        setProducts(r.data);
      } else if (t === 'inventory') {
        const r = await axios.get(`${API}/analytics/inventory-intel`, auth());
        setInv(r.data);
      } else if (t === 'operations') {
        const [a, b] = await Promise.all([
          axios.get(`${API}/analytics/delivery-economics?days=${d}`, auth()),
          axios.get(`${API}/analytics/returns-leakage?days=90`, auth()),
        ]);
        setDelivery(a.data); setReturnsL(b.data);
      } else if (t === 'customers') {
        const r = await axios.get(`${API}/analytics/customer-tiers?days=365`, auth());
        setCustomers(r.data);
      } else if (t === 'advisor') {
        const h = await axios.get(`${API}/analytics/advisor/history`, auth());
        setMessages(h.data.messages || []);
        loadDigest(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load analytics');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(tab, days); }, [tab, days, load]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadDigest = async (refresh) => {
    setDigestLoading(true);
    try {
      const r = await axios.get(`${API}/analytics/advisor/digest${refresh ? '?refresh=true' : ''}`, auth());
      setDigest(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate digest');
    } finally { setDigestLoading(false); }
  };

  const ask = async () => {
    const q = question.trim();
    if (!q || asking) return;
    setQuestion('');
    setAsking(true);
    setMessages(prev => [...prev, { role: 'user', content: q }, { role: 'assistant', content: '' }]);
    try {
      const res = await fetch(`${API}/analytics/advisor/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ question: q }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Advisor failed');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n\n');
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.delta) {
            setMessages(prev => {
              const copy = [...prev];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + ev.delta };
              return copy;
            });
          }
          if (ev.error) toast.error(ev.error);
        }
      }
    } catch (e) {
      toast.error(e.message || 'Advisor failed');
    } finally { setAsking(false); }
  };

  const saveCost = async (sku) => {
    const val = parseFloat(costEdits[sku]);
    if (!val || val <= 0) { toast.error('Enter a cost price greater than 0'); return; }
    setSavingSku(sku);
    try {
      await axios.put(`${API}/analytics/cost-price`, { sku, cost_price: val }, auth());
      toast.success(`Cost saved for ${sku}`);
      setMissingCosts(prev => ({ ...prev, count: prev.count - 1, items: prev.items.filter(i => i.sku !== sku) }));
      axios.get(`${API}/analytics/pnl?days=${days}`, auth()).then(r => setPnl(r.data)).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save cost');
    } finally { setSavingSku(null); }
  };

  return (
    <div className="space-y-4" data-testid="analytics-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Business Analytics</h1>
          <p className="text-sm text-wt-text-muted">Profit, inventory, operations & AI-powered insights</p>
        </div>
        {tab !== 'advisor' && tab !== 'customers' && tab !== 'inventory' && (
          <div className="flex gap-1 bg-wt-surface border border-wt-border rounded-lg p-1">
            {RANGES.map(r => (
              <button key={r} onClick={() => setDays(r)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium ${days === r ? 'bg-wt-gold text-black' : 'text-wt-text-muted hover:text-wt-text-primary'}`}
                data-testid={`range-${r}`}>
                {r}d
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-wt-surface border border-wt-border rounded-xl p-1 flex overflow-x-auto gap-1">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm whitespace-nowrap ${tab === t.id ? 'bg-wt-gold/15 text-wt-gold font-semibold' : 'text-wt-text-muted hover:text-wt-text-primary'}`}
            data-testid={`analytics-tab-${t.id}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-wt-gold" /></div>}

      {!loading && tab === 'profit' && pnl && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Revenue" value={fmt(pnl.revenue)} sub={`${pnl.orders} orders · AOV ${fmt(pnl.avg_order_value)}`} />
            <Kpi label="Gross Profit" value={fmt(pnl.gross_profit)} sub={`${pnl.gross_margin_pct}% margin · COGS ${fmt(pnl.cogs)}`} accent="text-emerald-400" />
            <Kpi label="Expenses + Refunds" value={fmt(pnl.expenses + pnl.refunds)} sub={`Expenses ${fmt(pnl.expenses)} · Refunds ${fmt(pnl.refunds)}`} accent="text-red-400" />
            <Kpi label="Net Profit" value={fmt(pnl.net_profit)} sub={`${pnl.net_margin_pct}% net margin`} accent={pnl.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'} />
          </div>
          {pnl.cogs_coverage_missing > 0 && (
            <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="cogs-warning">
              <AlertTriangle className="w-4 h-4" />
              {pnl.cogs_coverage_missing} SKU(s) have no cost price — profit is overstated. Fix them in the panel below for accurate margins.
            </div>
          )}
          {missingCosts && missingCosts.count > 0 && (
            <Section title={`Fix Missing Cost Prices (${missingCosts.count})`}
              right={<Badge variant="outline" className="text-amber-400 border-amber-500/40 text-[10px]">sorted by sales — fix top sellers first</Badge>}>
              <div className="overflow-x-auto max-h-96 overflow-y-auto" data-testid="missing-costs-table">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-wt-surface">
                    <tr className="text-left text-xs text-wt-text-muted border-b border-wt-border">
                      <th className="py-2 pr-3">Product</th>
                      <th className="py-2 pr-3 text-right">Sold 90d</th>
                      <th className="py-2 pr-3 text-right">Stock</th>
                      <th className="py-2 pr-3 text-right">Sell Price</th>
                      <th className="py-2 pr-3 text-right">Cost Price</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {missingCosts.items.map(r => (
                      <tr key={r.sku} className="border-b border-wt-border/40" data-testid={`missing-cost-row-${r.sku}`}>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            {r.image_url
                              ? <img src={r.image_url} alt="" className="w-8 h-8 rounded object-cover border border-wt-border" />
                              : <div className="w-8 h-8 rounded bg-wt-border/40 flex items-center justify-center"><Package className="w-4 h-4 text-wt-text-muted" /></div>}
                            <div className="min-w-0">
                              <p className="text-wt-text-primary truncate max-w-[200px]" title={r.name}>{r.name}</p>
                              <p className="text-[10px] text-wt-text-muted">{r.sku}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-right text-wt-text-primary">{r.sold_90d}</td>
                        <td className="py-2 pr-3 text-right text-wt-text-muted">{r.stock}</td>
                        <td className="py-2 pr-3 text-right text-wt-text-muted">{fmt(r.selling_price)}</td>
                        <td className="py-2 pr-3 text-right">
                          <Input type="number" min="0" step="0.01" placeholder="0.00"
                            value={costEdits[r.sku] ?? ''}
                            onChange={e => setCostEdits(prev => ({ ...prev, [r.sku]: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && saveCost(r.sku)}
                            className="h-8 w-24 text-right ml-auto"
                            data-testid={`cost-input-${r.sku}`} />
                        </td>
                        <td className="py-2 text-right">
                          <Button size="sm" className="h-8 bg-wt-gold text-black hover:bg-wt-gold/90"
                            disabled={savingSku === r.sku || !costEdits[r.sku]}
                            onClick={() => saveCost(r.sku)}
                            data-testid={`save-cost-${r.sku}`}>
                            {savingSku === r.sku ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
          <Section title="Revenue vs Gross Profit (daily)">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={pnl.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a33" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#888' }} />
                <YAxis tick={{ fontSize: 10, fill: '#888' }} />
                <Tooltip contentStyle={{ background: '#16161d', border: '1px solid #333' }} />
                <Legend />
                <Area type="monotone" dataKey="revenue" stroke="#c9a227" fill="#c9a22733" name="Revenue" />
                <Area type="monotone" dataKey="gross_profit" stroke="#34d399" fill="#34d39933" name="Gross Profit" />
              </AreaChart>
            </ResponsiveContainer>
          </Section>
          <div className="grid md:grid-cols-2 gap-4">
            <Section title="Profit by Channel">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={channels?.channels || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a33" />
                  <XAxis dataKey="channel" tick={{ fontSize: 10, fill: '#888' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#888' }} />
                  <Tooltip contentStyle={{ background: '#16161d', border: '1px solid #333' }} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#c9a227" name="Revenue" />
                  <Bar dataKey="gross_profit" fill="#34d399" name="Gross Profit" />
                </BarChart>
              </ResponsiveContainer>
              <MiniTable testid="channel-profit-table" cols={[
                { key: 'channel', label: 'Channel' },
                { key: 'orders', label: 'Orders', right: true },
                { key: 'revenue', label: 'Revenue', right: true, render: r => fmt(r.revenue) },
                { key: 'margin_pct', label: 'Margin %', right: true, cls: r => r.margin_pct >= 0 ? 'text-emerald-400 text-right' : 'text-red-400 text-right' },
              ]} rows={channels?.channels || []} />
            </Section>
            <Section title="Expenses by Category">
              <MiniTable testid="expense-table" cols={[
                { key: 'category', label: 'Category' },
                { key: 'amount', label: 'Amount', right: true, render: r => fmt(r.amount) },
              ]} rows={pnl.expense_breakdown || []} />
            </Section>
          </div>
        </>
      )}

      {!loading && tab === 'products' && products && (
        <div className="grid md:grid-cols-2 gap-4">
          <Section title="Top Money Makers (gross profit)">
            <MiniTable testid="top-products" cols={[
              { key: 'name', label: 'Product', render: r => <ProdCell r={r} /> },
              { key: 'qty', label: 'Sold', right: true },
              { key: 'gross_profit', label: 'Profit', right: true, render: r => fmt(r.gross_profit), cls: () => 'text-emerald-400 text-right' },
              { key: 'margin_pct', label: 'Margin %', right: true, render: r => r.has_cost ? `${r.margin_pct}%` : 'no cost' },
            ]} rows={products.top || []} />
          </Section>
          <Section title="Lowest Margin / Loss Makers">
            <MiniTable testid="bottom-products" cols={[
              { key: 'name', label: 'Product', render: r => <ProdCell r={r} /> },
              { key: 'qty', label: 'Sold', right: true },
              { key: 'margin_pct', label: 'Margin %', right: true, cls: r => r.margin_pct < 15 ? 'text-red-400 text-right' : 'text-amber-400 text-right' },
              { key: 'returns_qty', label: 'Returns', right: true, cls: r => r.returns_qty > 0 ? 'text-red-400 text-right' : 'text-wt-text-muted text-right' },
            ]} rows={products.bottom || []} />
          </Section>
        </div>
      )}

      {!loading && tab === 'inventory' && inv && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Reorder Now" value={inv.reorder_now.length} accent="text-amber-400" />
            <Kpi label="Dead Stock Value" value={fmt(inv.dead_stock_value)} sub={`${inv.dead_stock.length} SKUs, no sales 60d`} accent="text-red-400" />
            <Kpi label="Out of Stock (selling)" value={inv.stockouts.length} accent="text-red-400" />
            <Kpi label="Est. Daily Stock-out Loss" value={fmt(inv.est_daily_stockout_loss)} accent="text-red-400" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Section title="Reorder Suggestions">
              <MiniTable testid="reorder-table" cols={[
                { key: 'sku', label: 'Product', render: r => <ProdCell r={r} /> },
                { key: 'stock', label: 'Stock', right: true },
                { key: 'days_to_stockout', label: 'Days Left', right: true, cls: r => r.days_to_stockout < 7 ? 'text-red-400 text-right font-semibold' : 'text-amber-400 text-right' },
                { key: 'suggested_qty', label: 'Order Qty', right: true, cls: () => 'text-emerald-400 text-right font-semibold' },
              ]} rows={inv.reorder_now} />
            </Section>
            <Section title="Dead Stock (cash locked)">
              <MiniTable testid="dead-stock-table" cols={[
                { key: 'sku', label: 'Product', render: r => <ProdCell r={r} /> },
                { key: 'stock', label: 'Stock', right: true },
                { key: 'locked_value', label: 'Value Locked', right: true, render: r => fmt(r.locked_value), cls: () => 'text-red-400 text-right' },
                { key: 'last_sold', label: 'Last Sold', right: true, render: r => r.last_sold || 'never' },
              ]} rows={inv.dead_stock} />
            </Section>
          </div>
          <Section title="Selling Products Currently Out of Stock">
            <MiniTable testid="stockout-table" cols={[
              { key: 'sku', label: 'SKU' },
              { key: 'name', label: 'Product', render: r => <span className="truncate block max-w-[280px]">{r.name}</span> },
              { key: 'daily_velocity', label: 'Sales/Day', right: true },
              { key: 'est_lost_per_day', label: 'Est. Lost/Day', right: true, render: r => fmt(r.est_lost_per_day), cls: () => 'text-red-400 text-right' },
            ]} rows={inv.stockouts} />
          </Section>
        </>
      )}

      {!loading && tab === 'operations' && delivery && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Delivered" value={delivery.delivered_orders} accent="text-emerald-400" />
            <Kpi label="Failed Deliveries" value={delivery.failed_orders} sub={`Value at risk ${fmt(delivery.failed_value)}`} accent="text-red-400" />
            <Kpi label="Returns (90d)" value={returnsL?.returns ?? '—'} />
            <Kpi label="Refunds Paid (90d)" value={fmt(returnsL?.refund_total)} accent="text-red-400" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Section title="Driver / Courier Performance">
              <MiniTable testid="delivery-table" cols={[
                { key: 'name', label: 'Name', render: r => <span>{r.name} {r.is_courier && <Badge className="ml-1 text-[9px] bg-blue-500/20 text-blue-400">courier</Badge>}</span> },
                { key: 'delivered', label: 'Done', right: true },
                { key: 'success_rate', label: 'Success %', right: true, cls: r => r.success_rate >= 90 ? 'text-emerald-400 text-right' : 'text-amber-400 text-right' },
                { key: 'avg_delivery_hours', label: 'Avg Hrs', right: true, render: r => r.avg_delivery_hours ?? '—' },
              ]} rows={delivery.performers} />
            </Section>
            <Section title="Top Return Reasons & Products">
              <MiniTable testid="return-reasons" cols={[
                { key: 'reason', label: 'Reason' },
                { key: 'count', label: 'Pieces', right: true },
              ]} rows={returnsL?.by_reason || []} />
              <div className="mt-3" />
              <MiniTable testid="return-products" cols={[
                { key: 'name', label: 'Product', render: r => <span className="truncate block max-w-[180px]">{r.name}</span> },
                { key: 'returned_qty', label: 'Ret.', right: true },
                { key: 'return_rate_pct', label: 'Rate %', right: true, render: r => r.return_rate_pct ?? '—', cls: r => (r.return_rate_pct || 0) > 10 ? 'text-red-400 text-right' : 'text-wt-text-primary text-right' },
                { key: 'top_reason', label: 'Top Reason' },
              ]} rows={returnsL?.by_product || []} />
            </Section>
          </div>
        </>
      )}

      {!loading && tab === 'customers' && customers && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Customers (12m)" value={customers.total_customers} />
            <Kpi label="Repeat Customers" value={customers.repeat_customers} sub={`${customers.repeat_rate_pct}% repeat rate`} accent="text-emerald-400" />
            <Kpi label="One-time Buyers" value={customers.one_time_customers} sub="win-back opportunity" />
            <Kpi label="VIP Revenue Share" value={`${customers.vip_revenue_share_pct}%`} sub="top 10% of customers" accent="text-wt-gold" />
          </div>
          <Section title="Top Customers by Spend">
            <MiniTable testid="top-customers" cols={[
              { key: 'name', label: 'Customer' },
              { key: 'contact', label: 'Contact', render: r => <span className="text-wt-text-muted text-xs">{r.contact}</span> },
              { key: 'orders', label: 'Orders', right: true },
              { key: 'spent', label: 'Spent', right: true, render: r => fmt(r.spent), cls: () => 'text-wt-gold text-right font-semibold' },
            ]} rows={customers.top_customers} />
          </Section>
        </>
      )}

      {!loading && tab === 'advisor' && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Section title="This Week's Digest" right={
            <Button size="sm" variant="outline" onClick={() => loadDigest(true)} disabled={digestLoading}
              className="border-wt-border text-wt-text-muted" data-testid="refresh-digest-btn">
              {digestLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </Button>}>
            {digestLoading && !digest ? (
              <p className="text-sm text-wt-text-muted py-6 text-center">Analyzing your business data…</p>
            ) : (
              <div className="text-sm text-wt-text-primary whitespace-pre-wrap leading-relaxed" data-testid="digest-content">
                {digest?.digest || 'No digest yet — click refresh to generate.'}
              </div>
            )}
          </Section>
          <Section title="Ask Your Data">
            <div className="h-[380px] overflow-y-auto space-y-3 pr-1 mb-3" data-testid="advisor-chat">
              {messages.length === 0 && (
                <div className="text-xs text-wt-text-muted space-y-2 pt-4">
                  <p>Try asking:</p>
                  {['Which products lost money last month?', 'Which channel has the best margin?', 'What should I reorder this week?', 'How can I reduce returns?'].map(q => (
                    <button key={q} onClick={() => setQuestion(q)} className="block text-left text-wt-gold hover:underline">• {q}</button>
                  ))}
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`rounded-lg p-3 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-wt-gold/10 text-wt-text-primary ml-8' : 'bg-wt-bg border border-wt-border text-wt-text-primary mr-4'}`}>
                  {m.content || (asking && i === messages.length - 1 ? '…' : '')}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div className="flex gap-2">
              <Input value={question} onChange={e => setQuestion(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && ask()}
                placeholder="Ask about profit, products, inventory…"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
                data-testid="advisor-question-input" />
              <Button onClick={ask} disabled={asking || !question.trim()} className="bg-wt-gold text-black hover:bg-wt-gold/90" data-testid="advisor-ask-btn">
                {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}
